import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  AlertCircle, Clock, CheckCircle2, ChevronDown, ChevronRight, 
  User, Wrench, Download, Camera
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';

// ============== DEMO DATA ==============

const DEMO_SCENARIOS = {
  heavy: {
    vehicle: {
      reg: "WR67 XYZ",
      make: "BMW",
      model: "320d M Sport",
      year: 2017,
      mileage: 87432,
      colour: "Mineral Grey"
    },
    customer: {
      name: "James Mitchell",
      phone: "07XXX XXXXXX"
    },
    green_count: 42,
    redItems: [
      {
        id: "demo-red-1",
        label: "Front Brake Pads",
        price_inc_vat: 170.40,
        wear_percentage: 92,
        measurement_value: 1.8,
        explanation: {
          what_required: "Front brake pads measured at 1.8mm remaining. Minimum safe operating thickness is 3mm.",
          why_required: "Replace front brake pads with OEM-quality components to restore safe braking performance.",
          consequences: "Continued use will result in metal-to-metal contact, causing disc damage and significantly increased stopping distances."
        },
        education_insert: "Brake pads are designed with a wear indicator. Once pad material reaches approximately 2-3mm, braking efficiency reduces and the risk of disc scoring increases substantially.",
        photos: [],
        credibility_line: "Measured with calibrated digital gauge during inspection"
      },
      {
        id: "demo-red-2",
        label: "Front Brake Discs",
        price_inc_vat: 321.60,
        disc_wear_percentage: 88,
        measurement_value: 22.1,
        disc_min_thickness: 22.0,
        explanation: {
          what_required: "Front brake discs measured at 22.1mm. Manufacturer minimum specification is 22.0mm with visible lipping and heat scoring.",
          why_required: "Replace front brake discs to restore proper braking surface and heat dissipation.",
          consequences: "Discs at minimum thickness cannot effectively dissipate heat, leading to brake fade under heavy braking and potential disc failure."
        },
        education_insert: "Brake discs wear over time and develop a lip around the edge. Once they reach minimum thickness, they can no longer absorb and dissipate braking heat effectively.",
        photos: [],
        credibility_line: "Measured at multiple points with digital micrometer"
      },
      {
        id: "demo-red-3",
        label: "Front Suspension Drop Links",
        price_inc_vat: 106.80,
        explanation: {
          what_required: "Both front anti-roll bar drop links showing excessive play and worn bushes.",
          why_required: "Replace drop links to restore proper suspension geometry and handling characteristics.",
          consequences: "Worn drop links cause knocking noises, reduced handling precision, and accelerated tyre wear."
        },
        education_insert: "Drop links connect the anti-roll bar to the suspension, maintaining body control during cornering. When worn, they allow excessive body roll and create audible knocking.",
        photos: [],
        credibility_line: "Photographed during inspection - visible bush deterioration"
      },
      {
        id: "demo-red-4",
        label: "Rear Brake Pads",
        price_inc_vat: 141.60,
        wear_percentage: 85,
        measurement_value: 2.2,
        explanation: {
          what_required: "Rear brake pads measured at 2.2mm remaining. Below safe operating threshold.",
          why_required: "Replace rear brake pads to maintain balanced braking performance across all wheels.",
          consequences: "Uneven braking force distribution increases stopping distances and can cause vehicle instability under emergency braking."
        }
      },
      {
        id: "demo-red-5",
        label: "Drive Belt (Auxiliary)",
        price_inc_vat: 93.60,
        explanation: {
          what_required: "Auxiliary drive belt showing surface cracking and glazing. Age-related deterioration evident.",
          why_required: "Replace auxiliary drive belt to prevent sudden failure and loss of power steering, alternator, and air conditioning.",
          consequences: "Belt failure will result in immediate loss of power steering assistance, battery charging, and engine cooling fan operation."
        },
        photos: [],
        credibility_line: "Cracking visible on inspection - photographed"
      }
    ],
    amberItems: [
      {
        id: "demo-amber-1",
        label: "Rear Brake Discs",
        price_inc_vat: 237.60,
        disc_wear_percentage: 62,
        measurement_value: 9.2,
        disc_min_thickness: 8.0,
        explanation: {
          what_required: "Rear brake discs at 9.2mm. Minimum specification 8.0mm. Within tolerance but showing wear.",
          why_required: "Plan replacement within next service interval for optimal braking performance.",
          consequences: "Will require replacement at next service. Addressing now avoids return visit and labour duplication."
        },
        efficiency_indicator: "Labour already allocated for rear brake work - completing now reduces future costs"
      },
      {
        id: "demo-amber-2",
        label: "Air Filter",
        price_inc_vat: 38.40,
        explanation: {
          what_required: "Engine air filter showing contamination and restricted airflow.",
          why_required: "Replace air filter to maintain optimal fuel efficiency and engine protection.",
          consequences: "Restricted airflow reduces performance and fuel economy. Contaminants may pass to engine internals."
        }
      },
      {
        id: "demo-amber-3",
        label: "Pollen Filter",
        price_inc_vat: 33.60,
        explanation: {
          what_required: "Cabin pollen filter due for replacement based on mileage and visible contamination.",
          why_required: "Replace pollen filter to maintain cabin air quality and HVAC system efficiency.",
          consequences: "Reduced airflow to cabin, potential for musty odours, and increased strain on blower motor."
        },
        efficiency_indicator: "Quick replacement - no additional labour time"
      },
      {
        id: "demo-amber-4",
        label: "Front Wiper Blades",
        price_inc_vat: 50.40,
        explanation: {
          what_required: "Front wiper blades showing edge deterioration and streaking during operation.",
          why_required: "Replace wiper blades to restore clear visibility in wet conditions.",
          consequences: "Impaired visibility during rain increases accident risk. MOT failure likely if not addressed."
        }
      }
    ]
  },
  light: {
    vehicle: {
      reg: "KN21 ABC",
      make: "Volkswagen",
      model: "Golf",
      year: 2021,
      mileage: 28500,
      colour: "Pure White"
    },
    customer: {
      name: "Sarah Thompson",
      phone: "07XXX XXXXXX"
    },
    green_count: 58,
    redItems: [
      {
        id: "demo-light-red-1",
        label: "Front Wiper Blades",
        price_inc_vat: 45.60,
        explanation: {
          what_required: "Front wiper blades streaking and showing rubber edge deterioration.",
          why_required: "Replace to restore clear visibility in wet weather conditions.",
          consequences: "Current condition will likely result in MOT advisory or failure."
        }
      }
    ],
    amberItems: [
      {
        id: "demo-light-amber-1",
        label: "Air Filter",
        price_inc_vat: 33.60,
        explanation: {
          what_required: "Air filter due for replacement based on service schedule.",
          why_required: "Replace to maintain optimal engine efficiency and protection.",
          consequences: "Restricted airflow can reduce performance and fuel economy over time."
        }
      },
      {
        id: "demo-light-amber-2",
        label: "Pollen Filter",
        price_inc_vat: 28.80,
        explanation: {
          what_required: "Cabin filter showing contamination - replacement recommended.",
          why_required: "Replace to maintain cabin air quality.",
          consequences: "May cause reduced HVAC airflow and musty odours if left."
        },
        efficiency_indicator: "Quick replacement while vehicle is in workshop"
      }
    ]
  }
};

// ============== DEMO COMPONENT ==============

export default function MarketingDemo() {
  const [searchParams] = useSearchParams();
  const scenario = searchParams.get('scenario') || 'heavy';
  const [viewMode, setViewMode] = useState('customer'); // 'customer' | 'garage'
  const [showExecutiveSummary, setShowExecutiveSummary] = useState(true);
  const [expandedAmberItems, setExpandedAmberItems] = useState({});
  const [showAllAmberItems, setShowAllAmberItems] = useState(false);
  const [expandedEducationInserts, setExpandedEducationInserts] = useState({});

  // Get scenario data
  const data = useMemo(() => {
    const scenarioData = DEMO_SCENARIOS[scenario] || DEMO_SCENARIOS.heavy;
    const redTotal = scenarioData.redItems.reduce((sum, item) => sum + item.price_inc_vat, 0);
    const amberTotal = scenarioData.amberItems.reduce((sum, item) => sum + item.price_inc_vat, 0);
    
    return {
      ...scenarioData,
      red_total: redTotal,
      amber_total: amberTotal,
      total_estimate: redTotal + amberTotal,
      red_items: scenarioData.redItems,
      amber_items: scenarioData.amberItems
    };
  }, [scenario]);

  const toggleAmberItem = (itemId) => {
    setExpandedAmberItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const toggleEducationInsert = (itemId) => {
    setExpandedEducationInserts(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  // Overall status
  const getOverallVehicleStatus = () => {
    if (data.red_items.length > 0) {
      return { status: 'urgent', label: 'Urgent Repairs Identified', color: 'bg-amber-500' };
    }
    if (data.amber_items.length > 0) {
      return { status: 'attention', label: 'Attention Needed', color: 'bg-amber-400' };
    }
    return { status: 'good', label: 'Good Condition', color: 'bg-emerald-500' };
  };

  // Premium Inspection Item Card (RED items - always expanded)
  const PremiumInspectionItem = ({ item }) => (
    <div 
      key={item.id}
      className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden mb-4"
    >
      {/* Item Header */}
      <div className="p-5 border-b border-slate-100">
        <div className="flex justify-between items-start gap-4">
          <h4 className="font-semibold text-slate-800 text-base leading-snug">{item.label}</h4>
          <span className="font-bold text-slate-900 text-lg whitespace-nowrap">£{item.price_inc_vat.toFixed(2)}</span>
        </div>
      </div>
      
      <div className="p-5 space-y-4">
        {/* Wear Percentage Visual - for brake pads */}
        {item.wear_percentage !== undefined && (
          <div className="p-4 bg-slate-50 rounded-xl">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-slate-600 font-medium">Brake Pad Condition</span>
              <span className={`font-semibold ${
                item.wear_percentage >= 80 ? 'text-red-600' :
                item.wear_percentage >= 60 ? 'text-amber-600' : 'text-emerald-600'
              }`}>
                {item.wear_percentage}% worn
              </span>
            </div>
            <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all ${
                  item.wear_percentage >= 80 ? 'bg-red-500' :
                  item.wear_percentage >= 60 ? 'bg-amber-500' : 'bg-emerald-500'
                }`}
                style={{ width: `${item.wear_percentage}%` }}
              />
            </div>
            {item.measurement_value && (
              <p className="text-xs text-slate-500 mt-2">
                Pad thickness: {item.measurement_value}mm (minimum safe: 3mm)
              </p>
            )}
          </div>
        )}

        {/* Brake Disc Wear Visual */}
        {item.disc_wear_percentage !== undefined && (
          <div className="p-4 bg-slate-50 rounded-xl">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-slate-600 font-medium">Brake Disc Condition</span>
              <span className={`font-semibold ${
                item.disc_wear_percentage >= 80 ? 'text-red-600' :
                item.disc_wear_percentage >= 60 ? 'text-amber-600' : 'text-emerald-600'
              }`}>
                {item.disc_wear_percentage}% worn
              </span>
            </div>
            <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all ${
                  item.disc_wear_percentage >= 80 ? 'bg-red-500' :
                  item.disc_wear_percentage >= 60 ? 'bg-amber-500' : 'bg-emerald-500'
                }`}
                style={{ width: `${item.disc_wear_percentage}%` }}
              />
            </div>
            {item.measurement_value && item.disc_min_thickness && (
              <p className="text-xs text-slate-500 mt-2">
                Current: {item.measurement_value}mm | Minimum: {item.disc_min_thickness}mm
              </p>
            )}
          </div>
        )}

        {/* Premium Explanation Layout */}
        <div className="space-y-3">
          {item.explanation?.what_required && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">What we found</p>
              <p className="text-sm text-slate-700 leading-relaxed">{item.explanation.what_required}</p>
            </div>
          )}
          {item.explanation?.why_required && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">What we recommend</p>
              <p className="text-sm text-slate-700 leading-relaxed">{item.explanation.why_required}</p>
            </div>
          )}
          {item.explanation?.consequences && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Why it matters</p>
              <p className="text-sm text-slate-600 leading-relaxed italic">{item.explanation.consequences}</p>
            </div>
          )}
        </div>

        {/* Education Insert - expandable */}
        {item.education_insert && (
          <div className="pt-3 border-t border-slate-100">
            <button
              onClick={() => toggleEducationInsert(item.id)}
              className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
            >
              {expandedEducationInserts[item.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <span>View evidence & details</span>
            </button>
            {expandedEducationInserts[item.id] && (
              <div className="mt-3 p-4 bg-slate-50 rounded-xl text-sm text-slate-600 leading-relaxed">
                {item.education_insert}
              </div>
            )}
          </div>
        )}

        {/* Photos indicator */}
        {item.photos?.length > 0 && (
          <div className="pt-3 border-t border-slate-100">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Camera className="w-4 h-4" />
              <span>{item.photos.length} evidence photo(s) captured</span>
            </div>
            {item.credibility_line && (
              <p className="text-xs text-slate-400 mt-1">{item.credibility_line}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // ============== MAIN RENDER ==============
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f6f7f9' }}>
      {/* Small Demo Indicator - Floating badge */}
      <div className="fixed top-4 right-4 z-50">
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode(viewMode === 'customer' ? 'garage' : 'customer')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all shadow-lg ${
              viewMode === 'garage' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-white text-slate-700 border border-slate-200'
            }`}
          >
            {viewMode === 'garage' ? <><Wrench className="w-3 h-3 inline mr-1" />Garage View</> : <><User className="w-3 h-3 inline mr-1" />Customer View</>}
          </button>
          <Badge className="bg-indigo-600 text-white shadow-lg">DEMO</Badge>
        </div>
      </div>

      {/* Premium Header - Deep Navy Gradient (EXACT MATCH) */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-4 py-8">
          {/* Main Title */}
          <h1 className="text-2xl sm:text-3xl font-bold text-center tracking-tight">
            Vehicle Health Report
          </h1>
          
          {/* Vehicle Subline */}
          <p className="text-center text-slate-300 mt-3 text-sm sm:text-base font-medium">
            {data.vehicle.reg} • {data.vehicle.make} {data.vehicle.model} • {data.vehicle.mileage.toLocaleString()} miles
          </p>
          
          {/* Micro Line */}
          <p className="text-center text-slate-400 mt-2 text-xs tracking-wide">
            Inspected today • Evidence photographed
          </p>
          
          {/* Scenario Badge (Garage View only) */}
          {viewMode === 'garage' && (
            <div className="flex justify-center mt-4">
              <Badge variant="outline" className="bg-white/10 text-white/80 border-white/20">
                {scenario === 'heavy' ? 'Heavy Scenario (9 items)' : 'Light Scenario (3 items)'}
              </Badge>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* Hero Introduction Card (EXACT MATCH) */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-800 mb-4">
            We've carefully checked your vehicle
          </h2>
          <p className="text-slate-600 text-sm sm:text-base leading-relaxed mb-4">
            We've completed a comprehensive Safety & Reliability Inspection while your vehicle has been with us.
            The findings below are prioritised to clearly show what needs urgent attention and what should be planned next — so you can make informed decisions with confidence.
          </p>
          
          {/* Overall Vehicle Status Strip */}
          {(() => {
            const status = getOverallVehicleStatus();
            return (
              <div className={`${status.color} rounded-xl px-4 py-3 text-white text-center font-medium text-sm mb-4`}>
                {status.label}
              </div>
            );
          })()}
          
          {/* MOT Micro Line */}
          <div className="pt-4 border-t border-slate-100">
            <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">
              This inspection goes beyond the MOT by comparing key systems to manufacturer standards, not just minimum legal requirements.
            </p>
          </div>
        </div>

        {/* EXECUTIVE SUMMARY PANEL (EXACT MATCH) */}
        {(() => {
          const redCount = data.red_items.length;
          const amberCount = data.amber_items.length;
          const totalItems = redCount + amberCount;
          const redTotal = data.red_total;
          const amberTotal = data.amber_total;
          const combinedTotal = data.total_estimate;
          
          if (totalItems < 3) return null;
          
          return (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              {/* Panel Header */}
              <button
                onClick={() => setShowExecutiveSummary(!showExecutiveSummary)}
                className="w-full px-6 py-4 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <h3 className="font-bold text-slate-800 text-lg">Executive Summary</h3>
                <span className="text-sm text-slate-500 flex items-center gap-1">
                  {showExecutiveSummary ? (
                    <><ChevronDown className="w-4 h-4" /> Hide summary</>
                  ) : (
                    <><ChevronRight className="w-4 h-4" /> Show summary</>
                  )}
                </span>
              </button>
              
              {/* Panel Content - Collapsible */}
              <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
                showExecutiveSummary ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
              }`}>
                <div className="p-6 space-y-4">
                  {/* Urgent Safety Items Block */}
                  {redCount > 0 && (
                    <div className="bg-red-50/60 border border-red-100 rounded-xl p-5">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-red-800 flex items-center gap-2">
                            <AlertCircle className="w-5 h-5" />
                            Urgent Safety Items ({redCount})
                          </h4>
                          <p className="text-red-700/80 text-sm mt-1 leading-relaxed">
                            To maintain safe and reliable performance, we recommend these items are addressed as a priority.
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-red-800">£{redTotal.toFixed(2)}</p>
                          <p className="text-xs text-red-600">inc. VAT</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Maintenance Soon Block */}
                  {amberCount > 0 && (
                    <div className="bg-amber-50/60 border border-amber-100 rounded-xl p-5">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-amber-800 flex items-center gap-2">
                            <Clock className="w-5 h-5" />
                            Maintenance Soon ({amberCount})
                          </h4>
                          <p className="text-amber-700/80 text-sm mt-1 leading-relaxed">
                            These items are not urgent today, but are best planned in good time to prevent wear progressing into more costly repairs.
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-amber-800">£{amberTotal.toFixed(2)}</p>
                          <p className="text-xs text-amber-600">inc. VAT</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Combined Total Block */}
                  {redCount > 0 && amberCount > 0 && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-800">
                            If all recommended work is completed
                          </h4>
                          <p className="text-slate-500 text-sm mt-1">
                            Completing items during the same visit can often reduce duplicated labour and future inconvenience.
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-slate-900">£{combinedTotal.toFixed(2)}</p>
                          <p className="text-xs text-slate-500">total inc. VAT</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* URGENT SAFETY ITEMS (RED) - EXACT MATCH */}
        {data.red_items.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Section Header */}
            <div className="bg-red-50 border-b border-red-100 px-6 py-5">
              <h3 className="text-red-800 font-bold text-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Urgent Safety Items ({data.red_items.length})
              </h3>
              <p className="text-red-700/80 text-sm mt-1 leading-relaxed">
                These items require attention before the vehicle is driven further, to maintain safe and reliable operation.
              </p>
            </div>
            <div className="p-5">
              {data.red_items.map(item => <PremiumInspectionItem key={item.id} item={item} />)}
              <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between items-center">
                <span className="font-semibold text-slate-700">Urgent Safety Subtotal:</span>
                <span className="text-xl font-bold text-red-700">£{data.red_total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        {/* MAINTENANCE SOON (AMBER) - EXACT MATCH with collapsible items */}
        {data.amber_items.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Section Header */}
            <div className="bg-amber-50 border-b border-amber-100 px-6 py-5">
              <h3 className="text-amber-800 font-bold text-lg flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Maintenance Soon ({data.amber_items.length})
              </h3>
              <p className="text-amber-700/80 text-sm mt-1 leading-relaxed">
                Not urgent today, but best planned in good time to prevent wear progressing into more costly repairs.
              </p>
            </div>
            <div className="p-5">
              {/* Bundle summary */}
              <div className="bg-amber-50/50 border border-amber-200 rounded-xl p-4 mb-5">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-amber-900">Maintenance Bundle</p>
                    <p className="text-sm text-amber-700">{data.amber_items.length} preventative items identified</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-amber-800">£{data.amber_total.toFixed(2)}</p>
                    <p className="text-xs text-amber-600">inc. VAT</p>
                  </div>
                </div>
              </div>
              
              {/* Collapsible amber items (show first 3, or all if expanded) */}
              {(showAllAmberItems ? data.amber_items : data.amber_items.slice(0, 3)).map(item => (
                <div key={item.id} className="bg-white rounded-xl border border-slate-200 mb-3 overflow-hidden shadow-sm">
                  <button
                    className="w-full p-4 flex justify-between items-center hover:bg-slate-50 transition-colors"
                    onClick={() => toggleAmberItem(item.id)}
                  >
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        {expandedAmberItems[item.id] ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                        <span className="font-medium text-slate-800">{item.label}</span>
                      </div>
                      {item.efficiency_indicator && (
                        <div className="ml-6 mt-1">
                          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                            Cheaper to complete now
                          </span>
                        </div>
                      )}
                    </div>
                    <span className="font-bold text-slate-800">£{item.price_inc_vat.toFixed(2)}</span>
                  </button>
                  {expandedAmberItems[item.id] && (
                    <div className="p-5 bg-slate-50/50 border-t border-slate-100">
                      {/* Efficiency indicator detail */}
                      {item.efficiency_indicator && (
                        <div className="mb-4 p-2 bg-slate-100 border border-slate-200 rounded-lg">
                          <p className="text-xs text-slate-600">{item.efficiency_indicator}</p>
                        </div>
                      )}
                      
                      {/* Disc wear visual */}
                      {item.disc_wear_percentage !== undefined && (
                        <div className="mb-4 p-4 bg-white rounded-xl border border-slate-200">
                          <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-slate-600 font-medium">Brake Disc Condition</span>
                            <span className={`font-semibold ${
                              item.disc_wear_percentage >= 80 ? 'text-red-600' :
                              item.disc_wear_percentage >= 60 ? 'text-amber-600' : 'text-emerald-600'
                            }`}>
                              {item.disc_wear_percentage}% worn
                            </span>
                          </div>
                          <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all ${
                                item.disc_wear_percentage >= 80 ? 'bg-red-500' :
                                item.disc_wear_percentage >= 60 ? 'bg-amber-500' : 'bg-emerald-500'
                              }`}
                              style={{ width: `${item.disc_wear_percentage}%` }}
                            />
                          </div>
                          {item.measurement_value && item.disc_min_thickness && (
                            <p className="text-xs text-slate-500 mt-2">
                              Current: {item.measurement_value}mm | Minimum: {item.disc_min_thickness}mm
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Explanation */}
                      <div className="space-y-3">
                        {item.explanation?.what_required && (
                          <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">What we found</p>
                            <p className="text-sm text-slate-700 leading-relaxed">{item.explanation.what_required}</p>
                          </div>
                        )}
                        {item.explanation?.why_required && (
                          <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">What we recommend</p>
                            <p className="text-sm text-slate-700 leading-relaxed">{item.explanation.why_required}</p>
                          </div>
                        )}
                        {item.explanation?.consequences && (
                          <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Why it matters</p>
                            <p className="text-sm text-slate-600 leading-relaxed italic">{item.explanation.consequences}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {/* Show more button */}
              {data.amber_items.length > 3 && !showAllAmberItems && (
                <button
                  className="w-full py-4 text-center text-amber-700 hover:text-amber-900 text-sm font-medium border-t border-slate-200 mt-2"
                  onClick={() => setShowAllAmberItems(true)}
                >
                  View {data.amber_items.length - 3} more items
                </button>
              )}
              
              {showAllAmberItems && data.amber_items.length > 3 && (
                <button
                  className="w-full py-4 text-center text-amber-700 hover:text-amber-900 text-sm font-medium border-t border-slate-200 mt-2"
                  onClick={() => setShowAllAmberItems(false)}
                >
                  Show less
                </button>
              )}
            </div>
          </div>
        )}

        {/* Green Items Summary (EXACT MATCH) */}
        {data.green_count > 0 && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
            <span className="text-emerald-800">
              <strong>{data.green_count} items</strong> passed inspection with no issues found
            </span>
          </div>
        )}

        {/* Technical Report Download (EXACT MATCH) */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <button className="w-full flex items-center justify-center gap-2 text-slate-600 hover:text-slate-800 transition-colors">
            <Download className="w-5 h-5" />
            <span>Download Technical Report (PDF)</span>
          </button>
          <p className="text-xs text-center text-slate-400 mt-2">Reference / supporting evidence</p>
        </div>

        {/* Total (EXACT MATCH) */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 text-white rounded-2xl p-5 shadow-lg">
          <div className="flex justify-between items-center">
            <span className="text-lg">Total Estimate (inc. VAT):</span>
            <span className="text-2xl font-bold">£{data.total_estimate.toFixed(2)}</span>
          </div>
        </div>

        {/* Scenario Switcher (Demo only) */}
        <div className="flex justify-center gap-3 pt-4">
          <a 
            href="/demo?scenario=heavy"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              scenario === 'heavy' 
                ? 'bg-slate-800 text-white' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Heavy Scenario (9 items)
          </a>
          <a 
            href="/demo?scenario=light"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              scenario === 'light' 
                ? 'bg-slate-800 text-white' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Light Scenario (3 items)
          </a>
        </div>

        {/* CTA Section (Demo only) */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-2xl p-6 text-center mt-6">
          <h3 className="font-bold text-slate-800 text-lg mb-2">
            Ready to increase your authorisation rate?
          </h3>
          <p className="text-slate-600 text-sm mb-4">
            Yestimate transforms overwhelming inspections into clear, authorisable decisions.
          </p>
          <div className="flex justify-center gap-3">
            <a href="/" className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors">
              Request Demo
            </a>
            <a href="/" className="px-5 py-2.5 border border-indigo-200 text-indigo-700 hover:bg-indigo-50 font-medium rounded-lg transition-colors">
              Learn More
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-8 mt-8">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-slate-400 text-sm">
            Yestimate — Structured inspections. Clear decisions. Higher authorisations.
          </p>
        </div>
      </div>
    </div>
  );
}
